import React, { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, Grid, Typography, Alert, Chip } from '@mui/material';
import SchoolOutlinedIcon from '@mui/icons-material/SchoolOutlined';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import PeopleOutlineOutlinedIcon from '@mui/icons-material/PeopleOutlineOutlined';
import CodeRoundedIcon from '@mui/icons-material/CodeRounded';
import { useAuth } from '../../context/AuthContext';
import useAuthStore from '../../store/authStore';
import { formatAcademicDashboardChipLabel } from '../../utils/academicProfileFromApi';
import { isCbcFaculty, isTeachingFacultyRole } from '../../utils/facultyRole';
import { getFacultyCourses } from '../../services/courses.api';
import { getProjects, ctGetRecentActivity } from '../../services/api';
import useLabInternalStore from '../../store/useLabInternalStore';
// import InstitutionPageHeader from '../../components/InstitutionPageHeader';
import DashboardMetricCard from '../../components/Cards/DashboardMetricCard';
import CbcFacultyDashboard from './CbcFacultyDashboard';

/** Teaching faculty (non-CBC) and CBC on `/faculty/grading`: courses, labs, projects, light coding link. */
function RegularFacultyDashboard({ page = 'dashboard' }) {
  const { academicProfile } = useAuthStore();
  const fetchFacultyLabInternals = useLabInternalStore((s) => s.fetchFacultyLabInternals);
  const labs = useLabInternalStore((s) => s.facultyLabInternals);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [courseCount, setCourseCount] = useState(null);
  const [studentCount, setStudentCount] = useState(null);
  const [activityCount, setActivityCount] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError('');
      try {
        const [courses, , projectsRes, activityRes] = await Promise.all([
          getFacultyCourses().catch(() => []),
          fetchFacultyLabInternals().catch(() => null),
          getProjects().catch(() => ({ data: {} })),
          ctGetRecentActivity().catch(() => ({ data: {} })),
        ]);

        if (cancelled) return;

        const list = Array.isArray(courses) ? courses : [];
        setCourseCount(list.length);

        const projects = projectsRes?.data?.projects ?? [];
        const ids = new Set(
          projects.map((p) => p?.student?._id || p?.student?.id).filter(Boolean),
        );
        setStudentCount(ids.size);

        const events = activityRes?.data?.events ?? [];
        setActivityCount(Array.isArray(events) ? events.length : 0);
      } catch (e) {
        if (!cancelled) setError('Could not load dashboard metrics.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fetchFacultyLabInternals]);

  const activeLabs = useMemo(() => {
    if (!Array.isArray(labs)) return 0;
    return labs.filter((l) => (l?.status || 'ACTIVE').toUpperCase() !== 'INACTIVE').length;
  }, [labs]);

  const title = page === 'grading' ? 'Grading' : 'Dashboard';
  const subtitle =
    page === 'grading'
      ? 'Course, lab, and project context for evaluation workflows.'
      : 'Snapshot of your courses, labs, and students.';

  return (
    <Box>
      {/* <InstitutionPageHeader /> */}
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 1.5,
          mb: 0.5,
        }}
      >
        <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: '-0.02em' }}>
          {title}
        </Typography>
        <Chip
          size="small"
          variant="outlined"
          label={formatAcademicDashboardChipLabel(academicProfile)}
        />
      </Box>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 560 }}>
        {subtitle}
      </Typography>

      {error ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      ) : null}

      <Grid container spacing={2.5}>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            to="/faculty/courses"
            icon={<SchoolOutlinedIcon fontSize="small" />}
            label="Total courses"
            value={courseCount ?? '—'}
            loading={loading}
            hint="Manage"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            to="/faculty/lab-internals"
            icon={<ScienceOutlinedIcon fontSize="small" />}
            label="Active labs"
            value={loading ? null : activeLabs}
            loading={loading}
            hint="Lab internals"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            to="/faculty/grading"
            icon={<PeopleOutlineOutlinedIcon fontSize="small" />}
            label="Students"
            value={studentCount ?? '—'}
            loading={loading}
            hint="From projects"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <DashboardMetricCard
            to="/faculty/coding-dashboard"
            icon={<CodeRoundedIcon fontSize="small" />}
            label="Coding activity"
            value={activityCount ?? '—'}
            loading={loading}
            hint="Recent events"
          />
        </Grid>
      </Grid>
    </Box>
  );
}

function facultyRouteTail(pathname) {
  const p = (pathname || '/').replace(/\/+$/, '') || '/';
  const parts = p.split('/').filter(Boolean);
  return parts.length ? parts[parts.length - 1] : '';
}

const FacultyDashboard = () => {
  const { user } = useAuth();
  const { pathname } = useLocation();
  const tail = facultyRouteTail(pathname);

  if (!isTeachingFacultyRole(user)) {
    return (
      <Box sx={{ py: 4 }}>
        <Typography variant="body1" color="text.secondary" textAlign="center">
          This dashboard is for faculty. Use the menu to open another area.
        </Typography>
      </Box>
    );
  }

  // CBC coding home on `/faculty/dashboard` only; `/faculty/grading` uses the teaching snapshot (same as non-CBC grading).
  if (isCbcFaculty(user)) {
    if (tail === 'grading') {
      return <RegularFacultyDashboard page="grading" />;
    }
    return <CbcFacultyDashboard />;
  }

  return <RegularFacultyDashboard page={tail === 'grading' ? 'grading' : 'dashboard'} />;
};

export default FacultyDashboard;
